import type {
  DurationsRow,
  GlossaryRow,
  GlossarySection,
} from "@thalamos/common";

const BTP_HCP_REASON_NOT_CONSULTED =
  'Only applicable when Healthcare professional consulted = No. Used for "Reason not consulted" charts.\n\nOptions:\n\nImmediate Need for Care/Control\n\nNo Answer/Reply\n\nDid Not Consider It\n\nNot Sure Who to Call\n\nNot Deemed Necessary\n\nOther';

const DEFAULT_HCP_REASON_NOT_CONSULTED =
  'Only applicable when Healthcare professional consulted = No. Used for "Reason not consulted" charts.\n\nOptions:\n\nPatient is Violent\n\nImmediate Restraint Needed\n\nSingle Crewed/Solo Patrol\n\nPoor Connectivity\n\nNo Answer/Reply\n\nOther';

const DETENTION_IN_CUSTODY_ROW: GlossaryRow = {
  term: "Detention in Custody",
  definition:
    "Whether the person was detained while already in police custody (as opposed to detention occurring in the community).",
  dataContext: "Input by officer = Yes/No",
};

const POLICE_INVOLVEMENT_DURATION_ROW: DurationsRow = {
  term: "Police Involvement Duration",
  definition:
    "How long the police were involved in the incident from the initial call to the police to officers leaving the final place of safety",
  calculation: "dateTimeLeft-cadDateTime",
  cleaningRules:
    "Hide Incident if:\n\nCAD Time is missing\n\nTime of Police Leave is missing\n\nPolice Involvement Duration < 15 mins\n\nPolice Involvement Duration > 48 hours\n\nForm was started in error",
};

export function getDataGlossarySections(language: string): GlossarySection[] {
  const isBtp = language === "enBtp";
  return [
    {
      heading: "Incidents",
      variant: "standard",
      intro:
        "This section defines what an 'incident' is in MHForms and the key variants (S136, MCA, Voluntary), including how live vs completed workflow states are distinguished.",
      rows: [
        {
          term: "Incident",
          definition:
            "A care pathway in which police officers interact with an individual undergoing a mental health crisis.",
          dataContext:
            "An incident is the full process and data input in MHForms (with additional data in eMHA for the outcome form if applicable).\n\nAn incident is created when the police officer begins the form.",
        },
        {
          term: "S136 Incident",
          definition: "An incident in which Section 136 powers are used.",
          dataContext:
            'Incidents where the officer initiates a Section 136 form by selecting "Section 136 MHA" in Incident Triage.',
        },
        {
          term: "MCA Incident",
          definition:
            "An incident in which a Mental Capacity Act assessment takes place as part of the police intervention.",
          dataContext:
            'Incidents where the officer initiates a Section 1-6 Mental Capacity Act assessment form by selecting "Section 1-6 MCA" in Incident Triage and the subsequent incident which includes this action.',
        },
        {
          term: "Incident Status",
          definition:
            "The current point an incident has reached in the workflow. Whether it is still ongoing (and which step it has reached) or has been completed and closed/ended.",
          dataContext:
            "A MHForms generated label showing the current workflow stage of an incident.\n\nStatuses can be one of the following:\n\nUnknown\n\nIn progress\n\nAwaiting arrival\n\nArrived at a place of safety (s136)\n\nArrived at the emergency department (MCA)\n\nForm shared\n\nNHS form accepted\n\nPolice presence requested\n\nPolice presence not requested\n\nEnded",
        },
        {
          term: "Completed Incident",
          definition:
            "An incident which has been closed and marked as ended by the police user.",
          dataContext:
            "An incident is marked as ended when an officer selects 'Finish Form' and enters the subsequent place of safety leave date and time, and the outcome.\n\nA timestamp is assigned to the incident using the date/time the individual was detained by the police.",
        },
        {
          term: "Live Incident",
          definition:
            "An incident which is currently ongoing and has not been marked as ended.",
          dataContext:
            "An incident is live when it has an open form context created by an officer before it is finished.\n\nThe incident status is anything other than ended.\n\nA timestamp is assigned to the incident using the date/time the individual was detained by the police.",
        },
      ],
    },
    {
      heading: "Canonical Reporting Populations",
      variant: "standard",
      intro:
        "This section sets the standard reporting populations used across the dashboards. These definitions make denominators explicit and ensure metrics are comparable across pages.",
      rows: [
        {
          term: "Combined Reporting Populations",
          definition: "",
          dataContext: "",
          isGroupHeader: true,
        },
        {
          term: "Incident Reporting Base Population",
          definition:
            "All incidents regardless of whether they're still in progress or ended.",
          dataContext: "Form is created.",
        },
        {
          term: "Completed Incidents",
          definition:
            "Incidents that have been completed and closed by the police user.",
          dataContext: "The incident status is ended.",
        },
        {
          term: "Live Incidents",
          definition: "Incidents that are still ongoing and not yet closed.",
          dataContext: "The incident status is anything other than ended.",
        },
        {
          term: "S136 Reporting Populations",
          definition: "",
          dataContext: "",
          isGroupHeader: true,
        },
        {
          term: "S136 Reporting Base Population",
          definition:
            "All incidents that are Section 136 incidents regardless of whether they're still in progress or ended.",
          dataContext:
            "A s136 Incident, applying the Global reporting exclusions.",
        },
        {
          term: "Completed S136 Incidents",
          definition:
            "Section 136 incidents that have been completed and closed by the police user.",
          dataContext:
            "S136 reporting base population AND incident status is ended.",
        },
        {
          term: "Live S136 incidents",
          definition:
            "Section 136 incidents that are still ongoing and not yet closed.",
          dataContext:
            "S136 reporting base population AND incident status is anything other than ended.",
        },
        {
          term: "Outcome-recorded S136 incidents",
          definition:
            "Completed Section 136 incidents where a health outcome has been recorded.",
          dataContext:
            "Completed S136 incidents which has an associated complete Outcome Form.",
        },
        {
          term: "Extended S136 incidents",
          definition: "Section 136 incidents that have been extended.",
          dataContext:
            "S136 reporting base population AND the incident has been extended with an extension form.",
        },
        {
          term: "MCA Reporting Populations",
          definition: "",
          dataContext: "",
          isGroupHeader: true,
        },
        {
          term: "MCA Reporting Base Population",
          definition:
            "All incidents that are MCA incidents regardless of whether they're still in progress or ended.",
          dataContext:
            "An MCA Incident applying the Global reporting exclusions.",
        },
        {
          term: "Completed MCA Incidents",
          definition:
            "MCA incidents that have been completed and closed by the police user.",
          dataContext:
            "MCA reporting base population AND incident status is ended.",
        },
        {
          term: "Live MCA incidents",
          definition:
            "MCA incidents that are still ongoing and not yet closed.",
          dataContext:
            "MCA reporting base population AND incident status is anything other than ended.",
        },
        {
          term: "Global Reporting Exclusions",
          definition: "",
          dataContext: "",
          isGroupHeader: true,
        },
        {
          term: "Started in Error",
          definition:
            "Exclude incidents that were ended because they were started in error so they don't skew reporting.",
          dataContext:
            'Exclude incidents where the form was ended and the reason given was "started in error", unless explicitly stated otherwise.\n\nApplies across all metrics by default.',
        },
      ],
    },
    {
      heading: "Time Periods",
      variant: "standard",
      intro:
        "This section defines how metrics are assigned to time (day/week/month/year) and standardises common reporting windows (MTD, YTD, rolling 30 days, previous period, equivalent period last year). It also clarifies the difference between incident time attribution (typically detention time) and action time attribution (the timestamp of the action being measured).\n\nThe reporting windows are defined by the dashboard's inbuilt time filtering functionality and are presented here for context and user convenience.",
      rows: [
        {
          term: "Time Attribution Rule",
          definition:
            "Rule for which timestamp is used to assign incidents/actions to a day/week/month/year.",
          dataContext:
            "An incident (or action) is assigned to a day/week/month/year using the following timestamp rules:\n\nCompleted incidents: assigned using the date/time the individual was detained by the police.\n\nLive incidents: assigned using the date/time the individual was detained by the police.\n\nAction metrics: assigned using the timestamp recorded for the specific action (e.g., Arrived at POS date/time. Police leave POS date/time.",
        },
        {
          term: "Time Series",
          definition:
            "Trend view showing how metrics change over time, displayed as a sequence of time periods (monthly by default).",
          dataContext:
            "Values are aggregated into consecutive time buckets using the dashboard's time attribution rule. Grouping is by month by default, with optional week/day views where available.",
        },
        {
          term: "Current <period> (to-date)",
          definition:
            "From the start of the current calendar <period> up to now (to-date).",
          dataContext:
            'Calculated using start time of <period> to "now" timestamp.',
        },
        {
          term: "Current day",
          definition: "From the start of the current day up to now.",
          dataContext: 'Calculated using day start (00:00) to "now" timestamp.',
        },
        {
          term: "Current week",
          definition:
            "From the start of the current week up to now (week-to-date).",
          dataContext:
            'Calculated using week start (Monday 00:00) to "now" timestamp.',
        },
        {
          term: "Current month",
          definition:
            "From the start of the current calendar month up to now (month-to-date).",
          dataContext:
            'Calculated using month start (1st day 00:00) to "now" timestamp.',
        },
        {
          term: "Current year",
          definition:
            "From the start of the current calendar year up to now (year-to-date).",
          dataContext: 'Calculated using 1 Jan 00:00 to "now" timestamp.',
        },
        {
          term: "Last <period> (rolling window)",
          definition:
            "A rolling window ending now, with length equal to the <period>.",
          dataContext: "Time between now − <period> up to now.",
        },
        {
          term: "Last day",
          definition: "The most recent rolling day to now.",
          dataContext: "Time between now and 24 hours ago.",
        },
        {
          term: "Last week",
          definition: "The most recent rolling week to now.",
          dataContext: "Time between now and 7 days ago.",
        },
        {
          term: "Last month",
          definition: "The most recent month day to now.",
          dataContext: "Time between now and the same day of last month.",
        },
        {
          term: "Last quarter",
          definition: "The most recent rolling quarter to now.",
          dataContext: "Time between now and the same day 3 months ago.",
        },
        {
          term: "Last year",
          definition: "The last 365 days up to now (rolling window).",
          dataContext: "Time between now the same day 1 year ago.",
        },
        {
          term: "Previous <period> (last complete calendar period)",
          definition:
            "The most recent fully completed calendar <period> immediately before the current one.",
          dataContext:
            "Calculated from the start of the previous <period> to the start of the current <period>",
        },
        {
          term: "Previous calendar week",
          definition: "The previous completed calendar month.",
          dataContext:
            "From start of previous complete week to end of previous week (Mon 00:00 to Sun 23:59:59).",
        },
        {
          term: "Previous calendar month",
          definition: "The previous completed calendar month.",
          dataContext:
            "From 1st of previous complete month 00:00 to last day of last month 23:59:59.",
        },
        {
          term: "Previous calendar quarter",
          definition: "The previous completed calendar quarter.",
          dataContext:
            "From 1st of previous complete quarter 00:00 to last day of last quarter 23:59:59.",
        },
        {
          term: "Previous calendar year",
          definition: "The previous completed calendar year.",
          dataContext:
            "From 1 Jan last year 00:00 to 31 Dec last year 23:59:59.",
        },
        {
          term: "Equivalent Period (Previous Year)",
          definition:
            "The same start and end dates shifted back exactly one calendar year.",
          dataContext:
            "Current filter start time - 365 days AND end time - 365 days",
        },
      ],
    },
    {
      heading: "Place of Safety",
      variant: "standard",
      intro:
        "This section defines the health based locations that the police interact with during an incident, and related concepts in relation to the presented data. This will be Places of Safety (POS) for S136 incidents and Emergency Departments for MCA Incidents. POS/ED data is central to pathway reporting, but it is prone to input errors due to manual entry and inconsistent naming, so the definitions emphasise how sites are identified, how types are assigned, and the main data quality risks.",
      rows: [
        {
          term: "Place of Safety (POS)",
          definition:
            "The location where an individual is taken for assessment/care during a S136 incident.\n\nIn the context of an MCA Incident, 'Place of Safety' refers to the Emergency Department that the individual is taken for assessment/care.",
          dataContext:
            "Captured as a place name, address and postcode.\n\nDetails are either auto-populated from a predefined list (via search/selection) or entered manually by the officer.",
        },
        {
          term: "Place of Safety Type / Site Type",
          definition:
            "A categorisation of the type of location used as the place of safety, describing the setting where the individual is taken.",
          dataContext:
            "Captured as a selected type for the recorded place of safety. The available categories are:\n\nHBPOS (s136 suite)\n\nEmergency Department\n\nCustody\n\nCare Home\n\nMental Health Unit\n\nPrivate Home\n\nPolice Station\n\nOther\n\nSite type is a property of each individual Place of Safety, but it can also be entered by an officer. For reporting, the Place of Safety site type property takes precedence over officer-entered site type.\n\nFor MCA Incidents, the site type will always be Emergency Department.",
        },
        {
          term: "Emergency Department",
          definition:
            "(Specifically in a MCA Incident context)\n\nThe health based location where an individual is taken for assessment/care during a MCA incident.",
          dataContext:
            "Captured as a place name, address and postcode.\n\nDetails are either auto-populated from a predefined list (via search/selection) or entered manually by the officer.",
        },
        {
          term: "Number of POS Visited",
          definition:
            "The number of distinct places of safety (S136) or emergency departments (MCA) the individual is taken to during a single incident.",
          dataContext:
            "Calculated by the distinct number of sites an officer inputs during an incident.",
        },
        {
          term: "First Place of Safety",
          definition:
            "The first recorded place of safety (S136) or emergency department (MCA) the individual is taken to during the incident.",
          dataContext:
            "Identified by officer input for where the patient was taken to in the first instance.",
        },
        {
          term: "Final Place of Safety",
          definition:
            "The last recorded place of safety (S136) or emergency department (MCA) the individual is taken to during the incident.",
          dataContext:
            "Identified by officer input as the site in which the officers ultimately left.",
        },
      ],
    },
    {
      heading: "Detention Process",
      variant: "standard",
      intro:
        "This section covers operational and pathway attributes that describe where and how detention and conveyance occurred. These fields provide context for interpreting timelines and performance variation.",
      rows: [
        {
          term: "Location of Police Detention",
          definition:
            "The location where the person was detained by the police.",
          dataContext:
            "Recorded as address and postcode and used as a latitude and longitude for reporting.\n\nUsed to understand where detentions occur and to support geographic analysis of detention hotspots. Location is tied to the detention timestamp.",
        },
        ...(!isBtp ? [DETENTION_IN_CUSTODY_ROW] : []),
        {
          term: "Method of Transport",
          definition:
            "The transport method used to convey the person to the associated Place of Safety.",
          dataContext:
            "Selected by the officer from the following options:\n\nAmbulance\n\nPolice Vehicle\n\nOther Health Vehicle\n\nEscorted (Walked)\n\nMental Health Ambulance\n\nAlready at POS\n\nOther",
        },
        {
          term: "Reason police vehicle used to transport",
          definition:
            "The officer-stated reason a police vehicle was used to transport the patient to a Place of Safety.",
          dataContext:
            "Only applicable where Method of Transport = Police Vehicle\n\nOptions are:\n\nAmbulance Not Available in Time\n\nAmbulance Crew Refused\n\nRisk Assessment (Behaviour)\n\nAmbulance Not Requested\n\nAmbulance Retasked\n\nOther (please specify)",
        },
        {
          term: "Force used by police",
          definition:
            "Whether police force/restraint was used during the incident.",
          dataContext:
            "Used for safety and risk context.\n\nInput by officer = Yes/No",
        },
      ],
    },
    {
      heading: "HCP Consultation",
      variant: "standard",
      intro:
        "This section defines whether and how healthcare professionals were consulted during the incident, and what advice was given. These metrics are used to understand the role of clinical input in decision-making and pathway selection, so the definitions focus on when the questions apply (conditional logic) and how they should be reported.",
      rows: [
        {
          term: "Healthcare professional consulted",
          definition:
            "Whether the officer consulted a healthcare professional / clinical advice service as part of the incident.",
          dataContext:
            "Used to identify incidents where clinical input was sought prior to or during conveyance/arrival.\n\nInput by officer = Yes/No",
        },
        {
          term: "Reason a healthcare professional not consulted",
          definition:
            "The officer-reported reason it was not practicable to consult a healthcare professional when no consultation occurred.",
          dataContext: isBtp
            ? BTP_HCP_REASON_NOT_CONSULTED
            : DEFAULT_HCP_REASON_NOT_CONSULTED,
        },
      ],
    },
    {
      heading: "Demographics",
      variant: "standard",
      intro:
        'This section defines the demographic fields used across the dashboards to describe the population subject to Section 136 and MCA Incidents. These fields support breakdowns by absolute count, and where reliable denominators exist, rate calculations (e.g., detentions per 100k). Demographic metrics should clearly state whether they use raw counts or population-standardised rates, and whether geography is attributed using detention location or place of safety location (initially it will be detention location only).\n\nBecause demographic data is entered operationally and may be incomplete, all demographic charts will handle missing/invalid values consistently (typically by grouping into "Unknown") and, where relevant, include a completeness indicator elsewhere in the dashboard (e.g., % populated).',
      rows: [
        {
          term: "Age Group",
          definition:
            "The person's age at the time of detention, grouped into standard bands for reporting.",
          dataContext:
            "Used for demographic breakdown charts and normalised rates (per 100k) where population denominators exist.\n\nDerived from DOB, age at detention calculated, and bucketed into aggregated groups",
        },
        {
          term: "Gender",
          definition:
            "The person's self-identified gender as recorded by the officer.",
          dataContext:
            "Used for demographic breakdown charts.\n\nOptions:\n\nMale\n\nFemale\n\nNon-binary\n\nPrefer not to say",
        },
        {
          term: "Ethnicity Group",
          definition:
            "Aggregated ethnicity grouping aligned to reporting standards (i.e. Asian, Black, Mixed, White, Other, Unknown).",
          dataContext:
            'Derived primarily from Self Defined Ethnicity (SDE). If missing / "Prefer not to say" then use Ethnic Appearance (EA) as a fallback where defined.\n\nAggregated groups:\n\nAsian, Asian British or Asian Welsh\n\nBlack, Black British, Black Welsh, Caribbean or African\n\nMixed or Multiple ethnic groups\n\nWhite\n\nOther ethnic group\n\nUnknown',
        },
        {
          term: "SDE code",
          definition:
            "Self Defined Ethnicity code/value recorded for the person.",
          dataContext:
            "Used for detailed ethnicity breakdowns and as the primary source for Ethnicity Group aggregation.",
        },
        {
          term: "EA code",
          definition:
            "Ethnic Appearance code/value (IC codes) recorded for the person.",
          dataContext:
            'Used for an "ethnic appearance" breakdown tab and as a fallback for Ethnicity Group when SDE is missing/invalid.',
        },
      ],
    },
    {
      heading: "Compliance",
      variant: "standard",
      intro:
        "This section defines the terminology and inputs regarding police presence and the different inputs by different user types.\n\nThis section applies to S136 Incidents only.",
      rows: [
        {
          term: "Police Presence Requested",
          definition:
            "Whether health services requested the police to remain present at the place of safety (or requested additional police presence) as part of managing risk/safety during the incident.",
          dataContext:
            "Input from the health user once the incident is shared with them by an officer.\n\nUsed to report the proportion of incidents where police presence was requested, and (where available) whether the request was accepted/fulfilled.",
        },
        {
          term: "Reason for Requesting Police Presence",
          definition:
            "The reason health services give for requesting police presence at the place of safety.",
          dataContext:
            "Only applicable where Police Presence Requested = Yes\n\nOptions:\n\nTo protect people from an immediate risk to life, or being subject to/at risk of serious harm\n\nNot enough staff\n\nPerson to be transferred to another place of safety for assessment\n\nOther",
        },
        {
          term: "Police Presence Response (Health)",
          definition:
            "Whether police accepted the request and remained at the place of safety when requested as input by the health user.",
          dataContext:
            "The response from the health user as to whether the officer(s) remained or not.\n\nUser input = Yes/No",
        },
        {
          term: "Police Presence Response (Police)",
          definition:
            "Whether police accepted the request and remained at the place of safety when requested as input by the police user",
          dataContext:
            "The response from the police as to remain or not.\n\nOfficer input = Yes/No",
        },
      ],
    },
    {
      heading: "Durations",
      variant: "durations",
      intro:
        'This section defines the duration metrics used throughout the dashboards, including how each duration is calculated and the cleaning rules used to hide invalid records for each metric. Duration metrics may include both live and completed incidents where valid timestamps exist, but each duration has its own "valid data" population, so denominators must be applied per metric rather than assumed globally.',
      rows: [
        {
          term: "Durations which apply to all incidents",
          definition: "",
          calculation: "",
          cleaningRules: "",
          isGroupHeader: true,
        },
        {
          term: "Police Incident Duration",
          definition:
            "How long the police detention episode lasted, from when the individual was detained by the police to when the officer(s) left.",
          calculation: "dateTimeLeft - dateTimeOfDetention",
          cleaningRules:
            "Hide Incident if:\n\nTime of Police Detention is missing\n\nTime of Police Leave is missing\n\nPolice Incident Duration <= 15 mins\n\nPolice Incident Duration >= 48 hours",
        },
        ...(!isBtp ? [POLICE_INVOLVEMENT_DURATION_ROW] : []),
        {
          term: "Total Police POS / ED Duration",
          definition:
            "How long from the police arriving at the first place of safety to the police leaving the final place of safety",
          calculation:
            "dateTimeLeft (Final POS) - \n\narrivalDateTime (First POS)",
          cleaningRules:
            "Hide Incident if:\n\nTime of Arrival is missing\n\nTime of Police Leave is missing\n\nEntry to Police Leave Duration < 5 mins\n\nEntry to Police Leave Duration > 48 hours\n\nForm was started in error",
        },
        {
          term: "Police POS Visit Duration",
          definition:
            "How long the police spends associated with a single POS visit, measured from police arrival at that POS to police arrival at the next POS, or to the police leave time if there is no subsequent POS.",
          calculation:
            "For POS n\n\nIf there is a subsequent POS\n\narrivalDateTime (POS n+1) - arrivalDateTime (POS n)\n\nIf final POS\n\ndateTimeLeft - arrivalDateTime (POS n)",
          cleaningRules:
            "Hide Incident if:\n\nTime of Arrival for POS n is missing\n\nArrival Time for POS n+1 is missing AND Police Leave Time is missing\n\nPolice POS Visit Duration < 5 mins\n\nPolice POS Visit Duration > 48 hours\n\nForm was started in error",
        },
        {
          term: "Detention to Arrival at First POS Duration",
          definition:
            "How long it took to transport the individual to the first place of safety after being detained by the police.",
          calculation:
            "arrivalDateTime - dateTimeOfDetention\n\n(Arrival time is for First POS)",
          cleaningRules:
            "Hide Incident if:\n\nTime of Police Detention is missing\n\nTime of Arrival is missing for First POS\n\nConveyance Duration is negative\n\nConveyance Duration > 48 hours",
        },
        {
          term: "Detention to Arrival at Final POS Duration",
          definition:
            "How long it took to transport the individual to the final place of safety after being detained by the police.",
          calculation:
            "arrivalDateTime - dateTimeOfDetention\n\n(Arrival time is for Final POS)",
          cleaningRules:
            "Hide Incident if:\n\nTime of Police Detention is missing\n\nTime of Arrival is missing for Final POS\n\nConveyance Duration is negative\n\nConveyance Duration > 48 hours",
        },
      ],
    },
  ];
}
